import React, { useEffect, useMemo } from 'react'
import { Bitcoin, CreditCard, Landmark, Wallet } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import usePaymentStore from '../store/usePaymentStore'
import useAudioManager from '../../../hooks/useAudioManager'

/**
 * PaymentMethodSelector Component
 * Компонент для выбора способа оплаты
 */
const PaymentMethodSelector = ({ onMethodClick }) => {
  const { t } = useTranslation()
  const { playSound } = useAudioManager({
    pick: 'pick'
  })
  const { 
    selectedMethod, 
    availableMethods, 
    recentMethodIds,
    isLoadingMethods,
    setSelectedMethod,
    loadAvailableMethods
  } = usePaymentStore()

  // Загружаем доступные способы оплаты при монтировании
  useEffect(() => {
    loadAvailableMethods()
  }, [loadAvailableMethods])

  // Сортировка: недавние методы сверху, затем остальные в обычном порядке
  const sortedMethods = useMemo(() => {
    if (!recentMethodIds || recentMethodIds.size === 0) return availableMethods
    const recent = availableMethods.filter((m) => recentMethodIds.has(m.id))
    const rest = availableMethods.filter((m) => !recentMethodIds.has(m.id))
    const sorted = [...recent, ...rest]
    const uzs1Index = sorted.findIndex((m) => m.id === 'uzs')
    const uzs4Index = sorted.findIndex((m) => m.id === 'uzs4')
    if (uzs1Index !== -1 && uzs4Index !== -1) {
      if (recentMethodIds.has('uzs4') && !recentMethodIds.has('uzs')) {
        const [uzs1] = sorted.splice(uzs1Index, 1)
        sorted.splice(sorted.findIndex((m) => m.id === 'uzs4'), 0, uzs1)
      } else {
        const [uzs4] = sorted.splice(uzs4Index, 1)
        sorted.splice(sorted.findIndex((m) => m.id === 'uzs') + 1, 0, uzs4)
      }
    }
    return sorted
  }, [availableMethods, recentMethodIds])

  // Маппинг иконок (изображения для Stars и SBP, Lucide для остальных)
  const getMethodIcon = (method) => {
    if (method.id === 'telegram_stars') {
      return <img src="/images/payment/star.png" alt="Telegram Stars" className="w-8 h-8" />
    }
    if (method.id === 'sbp' || method.id === 'sbp2') {
      return <img src="/images/payment/sbp.png" alt="СБП" className="w-8 h-8" />
    }
    if (method.id === 'card') {
      return <img src="/images/payment/card-rub.png" alt="Карты РФ" className="w-8 h-8" />
    }
    if (method.id === 'cardww1' || method.id === 'cardww2' || method.id === 'cardww3') {
      // Для всех провайдеров международных карт используем одну иконку
      return <img src="/images/payment/card-ww.png" alt="VISA / MC World Wide" className="w-8 h-8" />
    }
    if (method.id === 'cardww4') {
      // Softkeys — банк
      return <Landmark size={28} className="w-8 h-8 text-white" />
    }
    if (method.id === 'cardww5' || method.id === 'cardww6') {
      // Paytree / EasyPay — Visa/MasterCard
      return <img src="/images/payment/card-ww.png" alt="Visa / MasterCard" className="w-8 h-8" />
    }
    if (method.id === 'google_pay') {
      return <img src="/images/payment/googlepay.png" alt="Google Pay" className="h-7" />
    }
    if (method.id === 'apple_pay') {
      return <img src="/images/payment/applepay.png" alt="Apple Pay" className="h-7" />
    }
    if (method.id === 'kzt_cardww4') {
      return <img src="/images/payment/kazakhstan.png" alt="KZT Visa / MasterCard" className="w-8 h-8" />
    }
    if (method.id === 'crypto') {
      return <img src="/images/payment/crypto.png" alt="Crypto" className="w-8 h-8" />
    }
    if (method.id === 'uzs' || method.id === 'uzs2' || method.id === 'uzs3' || method.id === 'uzs4' || method.id === 'uzs5' || method.id === 'uzs6' || method.id === 'uzs7') {
      return <img src="/images/payment/uzbekistan.png" alt="UZS" className="w-8 h-8" />
    }
    if (method.id === 'kzt1' || method.id === 'kzt2') {
      return <img src="/images/payment/kazakhstan.png" alt="KZT (Казахстан)" className="w-8 h-8" />
    }
    if (method.id === 'tjs1') {
      return <img src="/images/payment/tajikistan.png" alt="TJS (Таджикистан)" className="w-8 h-8" />
    }
    if (method.id === 'kgs1') {
      return <img src="/images/payment/kyrgyzstan.png" alt="KGS (Кыргызстан)" className="w-8 h-8" />
    }
    if (method.id === 'uah1') {
      return <img src="/images/payment/ukraine.png" alt="UAH (Україна)" className="w-8 h-8" />
    }
    if (method.id === 'byn1') {
      return <img src="/images/payment/belarus.png" alt="BYN (Беларусь)" className="w-8 h-8" />
    }
    if (method.id === 'skins') {
      return <img src="/images/payment/steam.png" alt="Steam Skins" className="w-8 h-8" />
    }
    
    // Для остальных используем Lucide иконки
    const iconMap = {
      Bitcoin: Bitcoin,
      CreditCard: CreditCard,
      Wallet: Wallet
    }
    const Icon = iconMap[method.icon]
    return Icon ? <Icon size={22} className="text-white" /> : null
  }

  const getMethodTitle = (method) => {
    if (method.id === 'sbp') return `${t('payment.methods.sbp')} #1`
    if (method.id === 'sbp2') return `${t('payment.methods.sbp')} #2`
    if (method.id === 'uzs2') return `${t('payment.methods.uzs')} #2`
    if (method.id === 'uzs3') return `${t('payment.methods.uzs')} #3`
    if (method.id === 'uzs4') return `${t('payment.methods.uzs')} #4`
    if (method.id === 'uzs5') return `${t('payment.methods.uzs')} #5`
    if (method.id === 'uzs6') return `${t('payment.methods.uzs')} #6`
    if (method.id === 'uzs7') return `${t('payment.methods.uzs')} #7`
    if (method.id === 'kzt2') return 'KZT #2'
    // Международные карты: cardww2 — основной провайдер (#1), cardww1 — резервный (#2)
    if (method.id === 'cardww2') return 'World Visa / MasterCard #1'
    if (method.id === 'cardww1') return 'World Visa / MasterCard #2'
    if (method.id === 'cardww3') return 'World Visa / MasterCard #3'
    if (method.id === 'cardww4') return t('payment.methods.bank')
    if (method.id === 'cardww5') return 'World Visa / MasterCard #5'
    if (method.id === 'cardww6') return 'World Visa / MasterCard #6'
    if (method.id === 'google_pay') return 'Google Pay'
    if (method.id === 'apple_pay') return 'Apple Pay'
    if (method.id === 'kzt_cardww4') return 'KZT Visa / MasterCard'

    return t(
      `payment.methods.${method.id
        .replace(/_([a-z])/g, (_, letter) => letter.toUpperCase())
        .replace('_', '')}`
    )
  }

  const getMethodDesc = (method) => {
    if (method.id === 'sbp') return `${t('payment.methods.sbpDesc')} #1`
    if (method.id === 'sbp2') return `${t('payment.methods.sbpDesc')} #2`
    if (method.id === 'uzs2' || method.id === 'uzs3' || method.id === 'uzs6' || method.id === 'uzs7') return t('payment.methods.uzsDesc')
    if (method.id === 'uzs4' || method.id === 'uzs5') return t('payment.methods.cardDesc')
    if (method.id === 'kzt2') return t('payment.kzt.amountKztLabel')
    // Для обоих провайдеров международных карт одинаковое описание
    if (method.id === 'cardww1' || method.id === 'cardww2' || method.id === 'cardww3') return t('payment.methods.cardDesc')
    if (method.id === 'cardww4' || method.id === 'google_pay' || method.id === 'apple_pay' || method.id === 'kzt_cardww4') return t('payment.methods.cardDesc')
    if (method.id === 'cardww5' || method.id === 'cardww6') return t('payment.methods.cardDesc')

    return t(
      `payment.methods.${method.id
        .replace(/_([a-z])/g, (_, letter) => letter.toUpperCase())
        .replace('_', '')}Desc`
    )
  }

  // Если загружается
  if (isLoadingMethods) {
    return (
      <div className="mb-6">
        <h2 className="text-lg font-semibold mb-4">{t('payment.selectMethod')}</h2>
        <div className="flex gap-2 overflow-hidden">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="flex-shrink-0 bg-surface rounded-lg px-3 py-2 animate-pulse flex items-center gap-2">
              <div className="w-6 h-6 bg-gray-600 rounded"></div>
              <div className="h-3 bg-gray-600 rounded w-20"></div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  // Если нет доступных способов
  if (availableMethods.length === 0) {
    return null
  }

  return (
    <div className="mb-6">
      <h2 className="text-lg font-semibold mb-4">{t('payment.selectMethod')}</h2>
      
      <div className="flex flex-col gap-2">
        {sortedMethods.map((method) => {
          const isSelected = selectedMethod === method.id
          const isRecent = recentMethodIds?.has(method.id)
          
          return (
            <button
              key={method.id}
              aria-label={`${getMethodTitle(method)} — ${getMethodDesc(method)}`}
              onClick={() => {
                playSound('pick')
                setSelectedMethod(method.id)
                onMethodClick?.(method.id)
              }}
              className={`
                flex-shrink-0 flex items-center gap-3 rounded-lg px-4 py-3 transition-all
                hover:scale-[1.02] active:scale-[0.98]
                ${isSelected 
                  ? 'ring-2 ring-accent-primary border-accent-primary/50 bg-accent-primary/10' 
                  : isRecent
                    ? 'border border-accent-primary/40 bg-accent-primary/5'
                    : 'border border-white border-opacity-10 hover:border-accent-primary/30 bg-surface'
                }
              `}
            >
              {/* Иконка */}
              <div className="flex items-center justify-center">
                {getMethodIcon(method)}
              </div>

              {/* Название */}
              <span className={`
                min-w-0 text-start text-sm font-medium break-words
                ${isSelected ? 'text-accent-primary' : 'text-white'}
              `}>
                <bdi>{getMethodTitle(method)}</bdi>
              </span>
            </button>
          )
        })}
      </div>
    </div>
  )
}

export default PaymentMethodSelector
